import { useEffect } from 'react';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

export const useSchemaOrg = (schemaData: SchemaOrgData | SchemaOrgData[]) => {
  useEffect(() => {
    const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];
    const scriptIds: string[] = [];

    schemas.forEach((schema, index) => {
      const scriptId = `schema-org-${index}-${Date.now()}`;
      scriptIds.push(scriptId);

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      scriptIds.forEach(id => {
        const script = document.getElementById(id);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, [schemaData]);
};

export const getLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: 'PG PW Malda - Physics Wallah Boys Hostel',
    alternateName: 'PW PG Malda',
    description: 'PG PW Malda is the best boys hostel on Malda Town Station Road with unlimited food, WiFi, and modern amenities. Perfect PG for Physics Wallah students, NEET aspirants, and working professionals. Top-rated PG in Malda with homely mess food.',
    image: 'https://static.readdy.ai/image/4b14e1bb1a7b82bff99828a132017b99/e7209b0a7b0292654e5adb3d65078f42.jpeg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Station Road, Malda Town',
      addressLocality: 'Malda',
      addressRegion: 'West Bengal',
      postalCode: '732101',
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 25.0104,
      longitude: 88.1408
    },
    url: siteUrl,
    telephone: '+917479023201',
    priceRange: '₹₹',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      opens: '00:00',
      closes: '23:59'
    },
    amenityFeature: [
      {
        '@type': 'LocationFeatureSpecification',
        name: 'WiFi',
        value: true
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: 'Food Service',
        value: true
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: '24/7 Electricity',
        value: true
      },
      {
        '@type': 'LocationFeatureSpecification',
        name: '24/7 Water Supply',
        value: true
      }
    ],
    keywords: 'pg pw malda, PG PW Malda, pw pg malda, Physics Wallah PG Malda, PW hostel Malda, PG in Malda, boys hostel Malda, Physics Wallah Malda, Malda NEET, Malda NEET Hostel, Malda PW, Malda Mess'
  };
};

export const getWebsiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PG PW Malda - Physics Wallah Boys Hostel NEET',
    alternateName: 'PW PG Malda',
    url: siteUrl,
    description: 'PG PW Malda - Premium boys hostel in Malda with world-class amenities for Physics Wallah students and NEET aspirants',
    publisher: {
      '@type': 'Organization',
      name: 'PG PW Malda'
    },
    keywords: 'pg pw malda, PG PW Malda, Physics Wallah PG Malda, Malda Boys Hostel, Malda NEET Hostel, Malda PW, Malda Mess, PG in Malda'
  };
};

export const getOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PG PW Malda - Physics Wallah Boys Hostel',
    alternateName: 'PW PG Malda',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+917479023201',
      contactType: 'Customer Service',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi', 'Bengali']
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Station Road, Malda Town',
      addressLocality: 'Malda',
      addressRegion: 'West Bengal',
      postalCode: '732101',
      addressCountry: 'IN'
    },
    description: 'PG PW Malda - Best boys hostel in Malda for Physics Wallah students and NEET aspirants with unlimited mess food'
  };
};

export const getOfferSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: 'PG PW Malda - Monthly Hostel Accommodation for Physics Wallah and NEET Students',
    description: 'PG PW Malda offers premium boys hostel accommodation in Malda with unlimited homely mess food and amenities for Physics Wallah students and NEET aspirants',
    price: '6000',
    priceCurrency: 'INR',
    availability: 'https://schema.org/InStock',
    url: siteUrl,
    seller: {
      '@type': 'Organization',
      name: 'PG PW Malda'
    },
    itemOffered: {
      '@type': 'Service',
      name: 'PG PW Malda - Hostel Accommodation for PW and NEET Students',
      description: 'PG PW Malda provides 3 times unlimited homely food, high-speed WiFi, new rooms & bathrooms, 24x7 electricity & water for Physics Wallah students and NEET aspirants in Malda'
    }
  };
};

export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};
